/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table as UITable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableLoad } from "@/components/ui/table-load";
import { EmptyTable } from "@/components/Datatable/components/EmptyTable";
import { ExtendedColumnDef } from "components/Datatable";
import styled from "styled-components";
import { getPagesToShow } from "../../../../../utils/pagination";
import { useAccountingAccountFilterActions } from "../../ducks/filter/hooks/actions";
import { useFilterState } from "../../ducks/filter/hooks/filterState";
import { Meta } from "src/@types/paginated";
import { IAccountingAccount } from "src/@types/account-accouting";

export default function Table({
  columns,
  meta,
  data,
  isFetching = false,
}: {
  meta: Meta;
  data: IAccountingAccount[];
  columns: ExtendedColumnDef<IAccountingAccount>[];
  isFetching?: boolean;
}) {
  const filterState = useFilterState();
  const { updateFilter } = useAccountingAccountFilterActions();
  const { filters } = filterState || { filters: {} };

  const table = useReactTable({
    data: data as any,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <div className="rounded-t-md border overflow-auto custom-scrollbar relative">
        <UITable className="relative">
          <TableHeader className="sticky top-0 bg-white z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isFetching ? (
              <TableLoad colSpan={columns.length} />
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="hover:bg-gray-100"
                >
                  {row.getVisibleCells().map((cell) => {
                    const columnDef = cell.column.columnDef as any;
                    return (
                      <TableCell key={cell.id} className={columnDef.className}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <EmptyTable text="Nenhuma conta contábil encontrada." />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </UITable>
      </div>
      <div className="flex w-full items-center justify-between pagination-container">
        <PaginationButtonsContainer>
          {data.length !== 0 && (
            <>
              <button
                className={`navigate-btn previous ${
                  meta.prev !== null ? "" : "disabled"
                }`}
                onClick={() =>
                  updateFilter({
                    key: "meta",
                    value: { page: filters.meta.page - 1 || 1 },
                  })
                }
                disabled={meta.prev === null}
              >
                Anterior
              </button>
              {getPagesToShow(meta).map((page) => (
                <span
                  key={page}
                  className={`pages ${
                    page === meta.currentPage ? "selected" : ""
                  }`}
                  onClick={() =>
                    updateFilter({
                      key: "meta",
                      value: { page: page },
                    })
                  }
                >
                  {page}
                </span>
              ))}
              <button
                className={`navigate-btn next ${
                  meta.next !== null ? "" : "disabled"
                }`}
                onClick={() =>
                  updateFilter({
                    key: "meta",
                    value: { page: filters.meta.page + 1 || 1 },
                  })
                }
                disabled={meta.next === null}
              >
                Próximo
              </button>
            </>
          )}
        </PaginationButtonsContainer>
        <span className="pagination-numbers">{`${
          data.length === 0 ? 0 : meta.currentPage ?? 0
        } de ${meta.lastPage} páginas`}</span>
      </div>
    </div>
  );
}

const PaginationButtonsContainer = styled.div`
  display: flex;
  font-size: 14px;

  .pages {
    display: flex;
    justify-content: center;
    padding: 8px 6px;
    border: 1px solid #dcdce3;
    border-left: none;
    border-top: none;
    min-width: 29px;

    &.selected {
      background-color: #f4c83e;
    }
  }

  .pages:not(.ocult) {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .navigate-btn {
    cursor: pointer;
    padding: 6px 12px;
    background-color: #2b0c1a;
    color: #fff;

    &.previous {
      border-radius: 0 0 0 8px;
    }

    &.next {
      border-radius: 0 0 8px 0;
    }

    &:hover {
      text-decoration: underline;
    }

    &.disabled {
      color: #8d91a2;
      background-color: #d9dae0;
      cursor: not-allowed;
      text-decoration: none;
    }
  }
`;
